// REACT
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// MUI
import { FormControl, Grid, InputLabel, MenuItem, Select, Typography, Chip, Stack } from '@mui/material'

// LIBS
import dayjs from 'dayjs'
import 'dayjs/locale/tr'
import 'dayjs/locale/en'

// SERVICES
import AppointmentService from '../../services/AppointmentService'

// UI
import WeeklySlotCalendar from '../../UI/WeeklySlotCalendar'

// UTILS
import { FREIGHT_AREAS, FREIGHT_AREAS_ANG_GATES, AREAS_FOR_3020_FREIGHTS, OPERATION_3020_GATE_MATCHINGS, freightAreaRequiresGate } from '../../utils/constants'
import { isFreightSlotSelectableAtNow } from '../../utils/helper'

const styles = {
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px'
    },
    boldText: {
        fontWeight: 'bold',
        fontSize: '16px'
    },
    leftArrow: {
        width: 0,
        height: 0,
        borderTop: '20px solid transparent',
        borderBottom: '20px solid transparent',
        borderRight: '20px solid rgb(2, 117, 216)',
        mr: '20px'
    },
    rightArrow: {
        width: 0,
        height: 0,
        borderTop: '20px solid transparent',
        borderBottom: '20px solid transparent',
        borderLeft: '20px solid rgb(2, 117, 216)',
        ml: '20px'
    },
}

const getWeekRange = (baseDate) => {
    const date = dayjs(baseDate)
    const dow = date.day() // 0..6 (Sun..Sat)
    let monday = date.subtract((dow + 6) % 7, 'day')
    // If weekend (Sat/Sun), show next week's Mon–Fri
    if (dow === 6 || dow === 0) {
        monday = monday.add(7, 'day')
    }
    const friday = monday.add(4, 'day') // Mon–Fri
    return { start: monday, end: friday }
}

const buildWeekDays = (monday, t) => {
    const dayNames = [t('monday'), t('tuesday'), t('wednesday'), t('thursday'), t('friday')]
    return Array.from({ length: 5 }).map((_, i) => {
        const d = monday.add(i, 'day')
        return { dateISO: d.format('YYYY-MM-DD'), label: dayNames[i] }
    })
}

const getEarliestWeekMonday = () => getWeekRange(dayjs()).start
const getLatestWeekStart = () => getWeekRange(dayjs().add(1, 'month')).start

const LoadReservationCalendarPanel = ({
    t,
    lang,
    appointmentDate,
    setAppointmentDate,
    freightArea,
    setFreightArea,
    gateNumber,
    setGateNumber,
    description,
    setDescription,
    selectedSlot,
    setSelectedSlot,
}) => {

    const appointmentService = useMemo(() => new AppointmentService(), [])

    const [weekStart, setWeekStart] = useState(getWeekRange(appointmentDate).start)
    const [weekEnd, setWeekEnd] = useState(getWeekRange(appointmentDate).end)
    const [weekDays, setWeekDays] = useState(buildWeekDays(getWeekRange(appointmentDate).start, t))

    const [slotsTemplate, setSlotsTemplate] = useState([])
    const [availabilityByDay, setAvailabilityByDay] = useState({}) // { dateISO: Set(slotNumber) }
    const [loading, setLoading] = useState(false)
    const [cache, setCache] = useState({}) // key: `${area}|${gate}|${weekStartISO}` -> { slotsTemplate, availabilityByDay }
    const inFlightKeyRef = useRef(null)
    /** Operasyon / kapı / hafta değişince artırılır; tamamlanan eski istekler state güncellemez. */
    const loadSeqRef = useRef(0)

    useEffect(() => {
        const { start, end } = getWeekRange(appointmentDate)
        setWeekStart(start)
        setWeekEnd(end)
        setWeekDays(buildWeekDays(start, t))
    }, [appointmentDate, t])

    // Ensure slot template is loaded once so the table never looks empty
    const templateLoadedRef = useRef(false)
    useEffect(() => {
        if (templateLoadedRef.current)
            return
        const loadTemplateOnce = async () => {
            const template = await fetchTemplateSlots()
            if (Array.isArray(template) && template.length > 0) {
                setSlotsTemplate(template)
                templateLoadedRef.current = true
            }
        }
        loadTemplateOnce()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchTemplateSlots = useCallback(async () => {
        // Use same shape as FreightCalendar (template)
        const result = await appointmentService.getSlots()
        return result?.data || []
    }, [appointmentService])

    const loadAvailability = useCallback(async () => {
        if (!freightArea) {
            setAvailabilityByDay({})
            setLoading(false)
            return
        }
        if (freightAreaRequiresGate(freightArea) && !gateNumber) {
            setAvailabilityByDay({})
            setLoading(false)
            return
        }

        const seq = ++loadSeqRef.current

        const cacheKey = `${freightArea}|${freightAreaRequiresGate(freightArea) ? gateNumber : 'all'}|${weekStart.format('YYYY-MM-DD')}`

        if (cache[cacheKey]) {
            if (seq !== loadSeqRef.current) return
            const hit = cache[cacheKey]
            setSlotsTemplate(hit.slotsTemplate)
            setAvailabilityByDay(hit.availabilityByDay)
            return
        }

        inFlightKeyRef.current = cacheKey
        setLoading(true)
        try {
            const weekKey = cacheKey
            const mondayISO = weekStart.format('YYYY-MM-DD')
            const fridayISO = weekEnd.format('YYYY-MM-DD')
            const slotParams = {
                freightArea,
                startDate: mondayISO,
                endDate: fridayISO,
            }
            if (freightAreaRequiresGate(freightArea) && gateNumber !== '' && gateNumber != null) {
                slotParams.gateNumber = gateNumber
            }

            let template = slotsTemplate
            if (!template || template.length === 0) {
                template = await fetchTemplateSlots(mondayISO)
                if (seq !== loadSeqRef.current) return
                if (Array.isArray(template) && template.length > 0) setSlotsTemplate(template)
            }

            const available = await appointmentService.getAvailableSlots(slotParams).then(r => r?.data || [])

            if (seq !== loadSeqRef.current) return

            const availability = {}
            const templateByTime = {}
                ; (template || []).forEach(s => {
                    templateByTime[`${s.START}-${s.END}`] = s.SLOT_NUMBER
                })
            const days = buildWeekDays(weekStart, t)
            days.forEach(d => { availability[d.dateISO] = new Set() })

            if (Array.isArray(available)) {
                // Single-day legacy shape: array of slots (only used when backend receives appointmentDate only)
            } else if (available && typeof available === 'object') {
                Object.entries(available).forEach(([dateKey, slotsArr]) => {
                    const dateISO = /^\d{4}-\d{2}-\d{2}$/.test(dateKey)
                        ? dateKey
                        : dayjs(dateKey, 'DD-MM-YYYY').format('YYYY-MM-DD')
                    if (!dateISO || !dayjs(dateISO).isValid()) return
                    if (!availability[dateISO]) availability[dateISO] = new Set()
                    ;(slotsArr || []).forEach((s) => {
                        let slotNum = s?.SLOT_NUMBER
                        if (slotNum === undefined || slotNum === null) slotNum = s?.slotNumber
                        if ((slotNum === undefined || slotNum === null) && s?.START && s?.END) {
                            slotNum = templateByTime[`${s.START}-${s.END}`]
                        }
                        if (slotNum !== undefined && slotNum !== null) availability[dateISO].add(Number(slotNum))
                    })
                })
            }

            if (seq !== loadSeqRef.current) return
            setAvailabilityByDay(availability)
            setCache(prev => ({ ...prev, [weekKey]: { slotsTemplate: template, availabilityByDay: availability } }))
        } finally {
            inFlightKeyRef.current = null
            if (seq === loadSeqRef.current) {
                setLoading(false)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [freightArea, gateNumber, weekStart, weekEnd, appointmentService, cache, slotsTemplate, t])

    useEffect(() => {
        loadAvailability()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [freightArea, gateNumber, description, weekStart])

    useEffect(() => {
        if (!selectedSlot?.SLOT_NUMBER || !appointmentDate) return
        const dISO = dayjs(appointmentDate).format('YYYY-MM-DD')
        if (!isFreightSlotSelectableAtNow(dISO, selectedSlot)) {
            setSelectedSlot({})
        }
    }, [appointmentDate, selectedSlot, setSelectedSlot])

    const onCellSelect = ({ dateISO, slot }) => {
        setAppointmentDate(dayjs(dateISO))
        setSelectedSlot(slot)
    }

    const onPrevWeek = () => {
        const newStart = weekStart.subtract(7, 'day')
        if (newStart.isBefore(getEarliestWeekMonday(), 'day')) return
        setWeekStart(newStart)
        setWeekEnd(newStart.add(4, 'day'))
        setWeekDays(buildWeekDays(newStart, t))
    }

    const onNextWeek = () => {
        const newStart = weekStart.add(7, 'day')
        if (newStart.isAfter(getLatestWeekStart(), 'day')) return
        setWeekStart(newStart)
        setWeekEnd(newStart.add(4, 'day'))
        setWeekDays(buildWeekDays(newStart, t))
    }

    return (
        <Grid container item xs={12} sx={{ mt: '20px' }}>
            {/* Header */}
            <Grid container item xs={12} sx={{ ...styles.header, justifyContent: 'space-between' }}>
                {/* Left: Area & Gate */}
                <Grid item sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {/* Area selection */}
                    <Grid item sx={{ width: '200px' }}>
                        <FormControl fullWidth>
                            <InputLabel id="area-label">{t('freightArea')}</InputLabel>
                            <Select
                                labelId="area-label"
                                value={freightArea || ''}
                                label={t('freightArea')}
                                onChange={(e) => {
                                    loadSeqRef.current += 1
                                    inFlightKeyRef.current = null
                                    setCache({})
                                    setSelectedSlot({})
                                    setAvailabilityByDay({})
                                    if (setGateNumber) setGateNumber('')
                                    if (setFreightArea) setFreightArea(e.target.value)
                                }}
                            >
                                {Object.keys(FREIGHT_AREAS).map((areaKey, i) => (
                                    <MenuItem key={i} value={FREIGHT_AREAS[areaKey]}>{t(areaKey)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item sx={{ width: '200px' }}>
                        {freightAreaRequiresGate(freightArea) && (
                            <FormControl fullWidth>
                                <InputLabel id="gate-label">{t('gateNumber')}</InputLabel>
                                <Select
                                    labelId="gate-label"
                                    value={gateNumber || ''}
                                    label={t('gateNumber')}
                                    onChange={(e) => setGateNumber(e.target.value)}
                                >
                                    {freightArea === FREIGHT_AREAS[3020]
                                        ? (AREAS_FOR_3020_FREIGHTS || []).map((area, i) => {
                                            const key = Object.keys(OPERATION_3020_GATE_MATCHINGS).find(k => OPERATION_3020_GATE_MATCHINGS[k] === area.value)
                                            const label = key ? t(key) : area.label
                                            return (
                                                <MenuItem key={i} value={area.value}>{label}</MenuItem>
                                            )
                                        })
                                        : (FREIGHT_AREAS_ANG_GATES[freightArea] || []).map((gate, i) => (
                                            <MenuItem key={i} value={gate}>{gate}</MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>
                        )}
                    </Grid>
                </Grid>

                {/* Right: Week arrows/label + Instructions */}
                <Grid item sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                        <Grid
                            item
                            sx={{
                                ...styles.leftArrow,
                                cursor: weekStart.isAfter(getEarliestWeekMonday(), 'day') ? 'pointer' : 'not-allowed',
                                borderRightColor: weekStart.isAfter(getEarliestWeekMonday(), 'day') ? 'rgb(2, 117, 216)' : '#bbb'
                            }}
                            onClick={() => { if (weekStart.isAfter(getEarliestWeekMonday(), 'day')) onPrevWeek() }}
                        />
                        <Typography variant="body1" sx={{ ...styles.boldText, mx: '12px' }}>
                            {(() => {
                                const locale = (lang === 'en' ? 'en' : 'tr')
                                const startDay = weekStart.locale(locale).format('DD')
                                const endDay = weekEnd.locale(locale).format('DD')
                                const startMonth = weekStart.locale(locale).format('MMMM')
                                const endMonth = weekEnd.locale(locale).format('MMMM')
                                return startMonth === endMonth
                                    ? `${startDay}/${endDay} ${startMonth}`
                                    : `${startDay} ${startMonth} / ${endDay} ${endMonth}`
                            })()}
                        </Typography>
                        <Grid
                            item
                            sx={{
                                ...styles.rightArrow,
                                cursor: weekStart.isBefore(getLatestWeekStart(), 'day') ? 'pointer' : 'not-allowed',
                                borderLeftColor: weekStart.isBefore(getLatestWeekStart(), 'day') ? 'rgb(2, 117, 216)' : '#bbb'
                            }}
                            onClick={onNextWeek}
                        />
                    </Grid>
                    {/* Instructions on the right */}

                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mt: '6px', justifyContent: 'flex-end' }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <Grid item sx={{ width: '18px', height: '12px', borderRadius: '3px', backgroundColor: '#d6f5e3', border: '1px solid #9dd6b6' }} />
                            <Typography variant="caption">{t('available') || 'Uygun'}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <Grid item sx={{ width: '18px', height: '12px', borderRadius: '3px', backgroundColor: '#e8e8e8', border: '1px solid #ccc' }} />
                            <Typography variant="caption">{t('slotTimePassed')}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <Grid item sx={{ width: '18px', height: '12px', borderRadius: '3px', backgroundColor: '#f0f0f0', border: '1px solid #ddd' }} />
                            <Typography variant="caption">{t('filled') || 'Dolu'}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <Grid item sx={{ width: '18px', height: '12px', borderRadius: '3px', backgroundColor: '#f0f0f0', border: '1px dashed #bbb' }} />
                            <Typography variant="caption">{t('loading') || 'Yükleniyor'}</Typography>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>

            {/* Instructions moved into header right; removed duplicate block */}

            {/* Calendar */}
            <WeeklySlotCalendar
                weekDays={weekDays}
                slots={slotsTemplate}
                availabilityByDay={availabilityByDay}
                selectedCell={selectedSlot?.SLOT_NUMBER && appointmentDate ? { dateISO: dayjs(appointmentDate).format('YYYY-MM-DD'), slotNumber: selectedSlot.SLOT_NUMBER } : null}
                onCellSelect={onCellSelect}
                loading={loading}
                disabled={freightAreaRequiresGate(freightArea) && !gateNumber}
                disabledReason={freightAreaRequiresGate(freightArea) ? t('selectGateFirst') : ''}
                t={t}
            />
        </Grid>
    )
}

export default LoadReservationCalendarPanel


